import { computed, Injectable } from "@angular/core";
import { metadata, Resources } from "../models/resources";
import { environment } from "@env/environment";
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, Subject } from "rxjs";
import { HttpClient, HttpParams } from "@angular/common/http";
import { AppService } from "@app/app.service";
import { catchError, map, shareReplay, switchMap, tap } from "rxjs/operators";
import { AWSS3Service } from "@app/shared/service/awsS3.service";
import { VersionsService } from "./versions.service";
import { Params } from "@angular/router";
import { CategoryService } from "@app/affiliates/services/category.service";
import { Rubro } from "@app/affiliates/models/model";
import { LanguageService } from "@shared/services/language.service";

interface response_resources {
  data: Resources;
  status: boolean;
  last_item?: any;
}

@Injectable({
  providedIn: "root",
})
export class ResourcesService {
  Resources: Resources[];
  Resources_filtered: Partial<Resources[]>;
  currentResource: Partial<Resources>;
  LEK: any;
  // Paginación separada para la fuente "recursos PUBLIC del owner" cuando el usuario
  // logueado no es el owner (ver getAllResources/loadMoreResources).
  LEK_PUBLISHER: any;
  LEK_FILTERED: any;
  LEK_FILTERED_PUBLISHER: any;

  get hasMore(): boolean {
    return !!(this.LEK || this.LEK_PUBLISHER);
  }
  categoryActive: Rubro | null = null;

  public _resources$ = new BehaviorSubject<Resources[]>([]);
  public resources$ = this._resources$.asObservable();
  private resourceRequest$ = new Subject<{ resource_id: string; ownerId: string }>();

  public resource$ = this.resourceRequest$.pipe(
    tap(() => {
      this.loadingResources = true;
    }),
    switchMap(({ resource_id, ownerId }) =>
      this.http
        .get<any>(
          `${this.BASE_API}/users/"${ownerId}"/resources?resource_id="${resource_id}"`
        )
        .pipe(
          map((response: any) => {
            if (!response?.status) {
              throw new Error("Invalid resource");
            }

            return {
              resource: response.data[0],
              lastEvaluatedKey: response.lastEvaluatedKey,
            };
          }),
          catchError((err) => {
            this.loadingResources = false;
            return EMPTY;
          })
        )
    ),
    tap(({ resource, lastEvaluatedKey }) => {
      this.currentResource = resource;
      this._versionsService.total_versions = resource.total_versions;
      this._versionsService.lastEvaluatedKey = lastEvaluatedKey;
      this.loadingResources = false;
    }),
    map(({ resource }) => resource),
    shareReplay(1)
  );

  loadResource(resource_id: string, ownerId: string = this.owner_id): void {
    this.resourceRequest$.next({ resource_id, ownerId });
  }

  public _resources_filtered$ = new BehaviorSubject<Resources[]>([]);
  public resources_filtered$ = this._resources_filtered$.asObservable();

  public filtered: boolean = false;
  private listRequest$ = new Subject<Params | undefined>();

  public list$ = this.listRequest$.pipe(
    switchMap((params) =>
      this.getAllResources(params).pipe(
        catchError(() => {
          this.loadingResources = false;
          return EMPTY;
        })
      )
    ),
    shareReplay(1)
  );

  private _openMenuId$ = new BehaviorSubject<string | null>(null);
  openMenuId$ = this._openMenuId$.asObservable();

  public _metadataLink$ = new BehaviorSubject<metadata>(null);
  public metadataLink$ = this._metadataLink$.asObservable();

  canEdit = computed(() => true);

  // Única cuenta autorizada a publicar recursos como PUBLIC o gestionarlos
  // (eliminar) desde el listado compartido — ceo@edutin.com. A diferencia de
  // owner_id (ver abajo), este valor es fijo y no depende del usuario logueado.
  readonly PUBLISHER_ID = "6830791";

  canPublish = computed(
    () => String(this._appService?.user_data?.id ?? "") === this.PUBLISHER_ID
  );

  // Cada recurso se asigna al usuario que lo está creando/gestionando: los
  // endpoints (list/get/create/update/delete) quedan scoped al usuario logueado.
  get owner_id(): string {
    return String(this._appService?.user_data?.id ?? "");
  }

  BASE_API = environment.urlAPI;
  BASE_API_AFFILIATE = environment.urlAPIAffiliate;
  GOOGLE_VIEWER_BASE = "https://docs.google.com/gview?embedded=true&url=";

  isEmptyResources: boolean = false;
  loadingResources: boolean = false;
  isEmpty$ = this.Resources$.pipe(map((list) => list.length === 0));

  constructor(
    private http: HttpClient,
    private _appService: AppService,
    private _versionsService: VersionsService,
    private _languageService: LanguageService
  ) {}

  /**
   * @param payload
   * @returns Observable
   *
   */
  createResource(
    payload: Partial<Resources>
  ): Observable<{ valid: boolean; data: Resources }> {
    const edutin_business_id = "281a17b0-3ab7-40c7-a4e2-ed636a32139f";

    payload = {
      ...payload,
      owner_id: this.owner_id,
      business_id: edutin_business_id,
      language_id: this._languageService.currentLanguageId,
      privacy: "PRIVATE",
    };

    const _payload = { data: payload };

    return this.http
      .post<response_resources>(
        `${this.BASE_API}/users/${JSON.stringify(this.owner_id)}/resources`,
        JSON.stringify(_payload)
      )
      .pipe(
        map((response: response_resources) => {
          let { status, data } = response;

          if (status) {
            data = { ...data, versions: [] };

            const current = this._resources$.value ?? [];
            this._resources$.next([data, ...current]);
            this.currentResource = data;

            return { valid: true, data };
          } else {
            return { valid: false, data: null };
          }
        })
      );
  }

  lastParamsString: string | null = null;

  // Tab "Mis recursos": cuando está activa, el listado omite la fuente del owner y
  // solo trae los recursos propios del usuario actual. Ver resource-list.component.ts.
  onlyMine: boolean = false;

  /**
   * GET crudo de una página de recursos para un owner_id puntual. No toca estado
   * del servicio — lo orquestan getAllResources()/loadMoreResources().
   */
  private fetchResourcesPage(
    ownerId: string,
    paramsString?: string,
    lek?: any
  ): Observable<{ data: Resources[]; lek: any }> {
    const langId = this._languageService.currentLanguageId;
    const sep = paramsString ? "&" : "";
    const prefix = paramsString ? `?${paramsString}` : "?";
    let URL = `${this.BASE_API}/users/${ownerId}/resources${prefix}${sep}language_id=${langId}`;

    if (lek) {
      URL += `&LastEvaluatedKey=${JSON.stringify(lek)}`;
    }

    return this.http.get<any>(URL).pipe(
      map((response: any) => {
        const { status, data, LastEvaluatedKey } = response;
        return {
          data: status ? (data as Resources[]) : [],
          lek: LastEvaluatedKey ?? null,
        };
      })
    );
  }

  /**
   * Primera página / cambio de filtro. Si el usuario logueado es el owner
   * (PUBLISHER_ID), trae solo su propio bucket (comportamiento histórico). Si no,
   * mergea sus propios recursos (siempre PRIVATE) + los PUBLIC del owner.
   */
  getAllResources(params?: Params): Observable<any> {
    if (!this.owner_id) {
      return of({ valid: false, data: [] });
    }

    this.loadingResources = true;
    this.isEmptyResources = false;
    const paramsString = params ? params.toString() : "";

    if (this.lastParamsString == paramsString && !this.loadingResources) {
      this.loadingResources = false;
      return of({
        valid: true,
        data: this._resources$.value as Resources[],
      });
    }

    this.lastParamsString = paramsString;
    this.LEK = null;
    this.LEK_PUBLISHER = null;

    const isPublisher = this.owner_id === this.PUBLISHER_ID;
    const includePublisher = !isPublisher && !this.onlyMine;

    type Page = { source: "own" | "publisher"; data: Resources[]; lek: any };

    const requests: Observable<Page>[] = [
      this.fetchResourcesPage(this.owner_id, paramsString).pipe(
        map((r): Page => ({ source: "own", ...r }))
      ),
    ];

    if (includePublisher) {
      requests.push(
        this.fetchResourcesPage(this.PUBLISHER_ID, paramsString).pipe(
          map(
            (r): Page => ({
              source: "publisher",
              data: r.data.filter((item) => item.privacy === "PUBLIC"),
              lek: r.lek,
            })
          )
        )
      );
    }

    return forkJoin(requests).pipe(
      map((results) => {
        results.forEach((r) => {
          if (r.source === "own") this.LEK = r.lek;
          if (r.source === "publisher") this.LEK_PUBLISHER = r.lek;
        });

        const merged = results.reduce(
          (acc, r) => acc.concat(r.data),
          [] as Resources[]
        );
        this._resources$.next(merged);
        this.loadingResources = false;
        this.isEmptyResources = merged.length === 0;
        return { valid: true, data: merged };
      })
    );
  }

  /**
   * Continuación de scroll infinito: pide la siguiente página solo de las fuentes
   * que aún tengan LastEvaluatedKey pendiente (propia y/o del owner) y concatena.
   */
  loadMoreResources(): Observable<any> {
    if (!this.hasMore) {
      return of({ valid: true, data: this._resources$.value });
    }

    this.loadingResources = true;
    const isPublisher = this.owner_id === this.PUBLISHER_ID;

    type Page = { source: "own" | "publisher"; data: Resources[]; lek: any };
    const requests: Observable<Page>[] = [];

    if (this.LEK) {
      requests.push(
        this.fetchResourcesPage(this.owner_id, this.lastParamsString, this.LEK).pipe(
          map((r): Page => ({ source: "own", ...r }))
        )
      );
    }

    if (!isPublisher && !this.onlyMine && this.LEK_PUBLISHER) {
      requests.push(
        this.fetchResourcesPage(
          this.PUBLISHER_ID,
          this.lastParamsString,
          this.LEK_PUBLISHER
        ).pipe(
          map(
            (r): Page => ({
              source: "publisher",
              data: r.data.filter((item) => item.privacy === "PUBLIC"),
              lek: r.lek,
            })
          )
        )
      );
    }

    if (!requests.length) {
      this.loadingResources = false;
      return of({ valid: true, data: this._resources$.value });
    }

    return forkJoin(requests).pipe(
      map((results) => {
        results.forEach((r) => {
          if (r.source === "own") this.LEK = r.lek;
          if (r.source === "publisher") this.LEK_PUBLISHER = r.lek;
        });

        const appended = results.reduce(
          (acc, r) => acc.concat(r.data),
          [] as Resources[]
        );
        const concatenated = [...(this._resources$.value || []), ...appended];
        this._resources$.next(concatenated);
        this.loadingResources = false;
        return { valid: true, data: concatenated };
      })
    );
  }

  loadByCategoryOrTab(params?: Params) {
    this.listRequest$.next(params);
  }

  hasMoreFiltered = true;
  private activeSearchQuery: string | null = null;
  private ownSearchExhausted = false;
  private publisherSearchExhausted = false;

  /**
   * GET crudo de una página de búsqueda para un owner_id puntual. No toca estado
   * del servicio.
   */
  private fetchSearchPage(
    ownerId: string,
    query: string,
    lastItem?: any
  ): Observable<{ data: Resources[]; lastItem: any }> {
    const params = new URLSearchParams({ query: JSON.stringify(query) });

    if (lastItem) {
      params.set("last_item", JSON.stringify(lastItem));
    }

    const langId = this._languageService.currentLanguageId;
    params.set("language_id", String(langId));

    const url = `${this.BASE_API}/users/${ownerId}/resources?${params}`;

    return this.http.get(url).pipe(
      map((response: any) => {
        const data = (response as any)?.data ?? {};
        return {
          data: (data.items ?? []) as Resources[],
          lastItem: data.last_item ?? null,
        };
      })
    );
  }

  searchResources(
    query?: string
  ): Observable<{ valid: boolean; data: any[]; last_item?: any }> {
    const isNewSearch = query !== undefined;
    const cleanQuery = (query ?? this.activeSearchQuery)?.trim();

    // ─────────────────────────────────────────────
    // 1. Sin query => limpiar estado
    // ─────────────────────────────────────────────
    if (!cleanQuery) {
      this.filtered = false;
      this.hasMoreFiltered = true;
      this.LEK_FILTERED = null;
      this.LEK_FILTERED_PUBLISHER = null;
      this.ownSearchExhausted = false;
      this.publisherSearchExhausted = false;
      this.activeSearchQuery = null;
      this._resources_filtered$.next([]);
      return of({ valid: true, data: [] });
    }

    // ─────────────────────────────────────────────
    // 2. NUEVA búsqueda (aunque sea el mismo texto)
    // ─────────────────────────────────────────────
    if (isNewSearch) {
      this.filtered = true;
      this.hasMoreFiltered = true;
      this.LEK_FILTERED = null;
      this.LEK_FILTERED_PUBLISHER = null;
      this.ownSearchExhausted = false;
      this.publisherSearchExhausted = false;
      this._resources_filtered$.next([]);
      this.activeSearchQuery = cleanQuery;
    }

    // ─────────────────────────────────────────────
    // 3. Sin más datos → no disparar request
    // ─────────────────────────────────────────────
    if (!this.hasMoreFiltered) {
      return of({ valid: true, data: [] });
    }

    this.loadingResources = true;

    const isPublisher = this.owner_id === this.PUBLISHER_ID;

    type Page = { source: "own" | "publisher"; data: Resources[]; lastItem: any };
    const requests: Observable<Page>[] = [];

    if (!this.ownSearchExhausted) {
      requests.push(
        this.fetchSearchPage(
          this.owner_id,
          this.activeSearchQuery,
          this.LEK_FILTERED
        ).pipe(map((r): Page => ({ source: "own", ...r })))
      );
    }

    if (!isPublisher && !this.publisherSearchExhausted) {
      requests.push(
        this.fetchSearchPage(
          this.PUBLISHER_ID,
          this.activeSearchQuery,
          this.LEK_FILTERED_PUBLISHER
        ).pipe(
          map(
            (r): Page => ({
              source: "publisher",
              data: r.data.filter((item) => item.privacy === "PUBLIC"),
              lastItem: r.lastItem,
            })
          )
        )
      );
    }

    if (!requests.length) {
      this.loadingResources = false;
      this.hasMoreFiltered = false;
      return of({ valid: true, data: [] });
    }

    return forkJoin(requests).pipe(
      map((results) => {
        results.forEach((r) => {
          if (r.source === "own") {
            this.LEK_FILTERED = r.lastItem;
            if (!r.lastItem) this.ownSearchExhausted = true;
          }
          if (r.source === "publisher") {
            this.LEK_FILTERED_PUBLISHER = r.lastItem;
            if (!r.lastItem) this.publisherSearchExhausted = true;
          }
        });

        this.hasMoreFiltered = !(
          this.ownSearchExhausted &&
          (isPublisher || this.publisherSearchExhausted)
        );

        const resources = results.reduce(
          (acc, r) => acc.concat(r.data),
          [] as Resources[]
        );

        if (!resources.length) {
          this.loadingResources = false;
          return { valid: true, data: [] };
        }

        const previous = this._resources_filtered$.value ?? [];
        const merged = [...previous, ...resources];

        const unique = merged.filter(
          (item, index, arr) => index === arr.findIndex((x) => x.id === item.id)
        );

        this._resources_filtered$.next(unique);

        this.loadingResources = false;

        return {
          valid: true,
          data: resources,
          last_item: this.LEK_FILTERED ?? this.LEK_FILTERED_PUBLISHER,
        };
      })
    );
  }

  clearFilter() {
    this.filtered = false;
    this._resources_filtered$.next([]);
  }

  get Resources$() {
    return this.filtered
      ? this._resources_filtered$.asObservable()
      : this._resources$.asObservable();
  }

  /**
   *
   * @param resource_id
   * @returns
   */
  getResource(
    resource_id: string,
    ownerId: string = this.owner_id,
    LEK?: Partial<any>
  ): Observable<response_resources> {
    if (!resource_id || !ownerId) return;

    const params = new URLSearchParams();
    params.set("resource_id", `"${resource_id}"`);

    if (LEK) {
      params.set("lastEvaluatedKey", `"${LEK}"`);
    }

    return this.http
      .get<Partial<Resources>>(
        `${this.BASE_API}/users/"${ownerId}"/resources?${params}`
      )
      .pipe(
        map((response: any) => {
          const { status, data, lastEvaluatedKey } = response;
          if (status) {
            this.currentResource = data;
            this._versionsService.total_versions = data[0].total_versions;
            this._versionsService.lastEvaluatedKey = lastEvaluatedKey;

            return { status: true, data };
          } else {
            return { status: false, data: null };
          }
        })
      );
  }

  updateResource(
    resource_id: string,
    payload: { data: Partial<Resources> },
    ownerId: string = this.owner_id
  ): Observable<any> {
    if (!payload) return;

    return this.http
      .put(
        `${this.BASE_API}/users/"${ownerId}"/resources/"${resource_id}"`,
        payload
      )
      .pipe(
        map((response: any) => {
          const { status } = response;
          if (status) {
            this.currentResource = { ...this.currentResource, ...payload };
            return { valid: true, resource_updated: this.currentResource };
          } else {
            return { valid: false, data: null };
          }
        })
      );
  }

  deleteResource(
    resource_id: string,
    ownerId: string = this.owner_id
  ): Observable<any> {
    return this.http
      .delete(
        `${this.BASE_API}/users/"${ownerId}"/resources/"${resource_id}"`
      )
      .pipe(
        map((response: any) => {
          const { status } = response;
          if (status) {
            const filtered = this._resources$.value.filter(
              (r) => r.id != resource_id
            );
            this._resources$.next(filtered);
            return { valid: true };
          } else {
            return { valid: false };
          }
        })
      );
  }

  getMetadataURL(url: string): Observable<any> {
    const params = new HttpParams().set("url", `"${url}"`);
    return this.http
      .get(`${this.BASE_API_AFFILIATE}/url/metadata`, { params })
      .pipe(
        map((res: any) => {
          const { status, data } = res;
          if (status) {
            return { valid: true, metadata: data };
          } else {
            return { valid: false, metadata: null };
          }
        })
      );
  }

  isResourceType(
    type?: string | null,
    contentType?: string | null,
    Resource?: Resources
  ): boolean {
    const resource = this.currentResource || Resource;
    if (!resource) return false;

    const matchesType = type ? resource.type === type : true;
    const matchesContent = contentType
      ? resource.content?.[0]?.type === contentType
      : true;

    return matchesType && matchesContent;
  }

  openModal(id: string) {
    this._openMenuId$.next(id);
  }

  closeModal() {
    this._openMenuId$.next(null);
  }
}
